import React, { useEffect, useState } from 'react';
import { FiArrowRight, FiX } from 'react-icons/fi';
import { MdCheckCircle, MdRocketLaunch } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import apiAxios from 'helpers/apiAxios';

// A release is only promoted for 10 days after it ships.
const BANNER_LIFETIME_MS = 10 * 24 * 60 * 60 * 1000;

const ReleaseNotificationBanner = () => {
  const navigate = useNavigate();
  const [latestRelease, setLatestRelease] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchLatestRelease = async () => {
      try {
        const response = await apiAxios.get('/changelog_entries/latest');
        if (mounted && response.data?.data) setLatestRelease(response.data.data);
      } catch {
        // The banner is non-blocking and should stay silent when the request fails.
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchLatestRelease();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!latestRelease || loading) return undefined;

    const version = latestRelease.attributes?.version;
    let dismissedRelease = {};

    try {
      dismissedRelease = JSON.parse(localStorage.getItem('dismissed_release') || '{}');
    } catch {
      localStorage.removeItem('dismissed_release');
    }

    const isDismissedForCurrentRelease = dismissedRelease.version === version;
    setDismissed(isDismissedForCurrentRelease);

    if (isDismissedForCurrentRelease) return undefined;

    const visibilityTimer = window.setTimeout(() => setIsVisible(true), 80);
    return () => window.clearTimeout(visibilityTimer);
  }, [latestRelease, loading]);

  const handleDismiss = () => {
    setIsVisible(false);
    window.setTimeout(() => {
      localStorage.setItem(
        'dismissed_release',
        JSON.stringify({
          version: latestRelease.attributes.version,
          dismissedAt: new Date().toISOString(),
        }),
      );
      setDismissed(true);
    }, 220);
  };

  const handleViewChangelog = () => {
    navigate(`/documentation/changelog/${latestRelease.attributes.version}`);
  };

  if (dismissed || loading || !latestRelease) return null;

  const { version, title, highlights = [], released_at } = latestRelease.attributes;
  const releaseAge = Date.now() - new Date(released_at).getTime();

  // Missing dates fail open; a known release older than the promotion window is hidden.
  if (!Number.isNaN(releaseAge) && releaseAge > BANNER_LIFETIME_MS) return null;

  const isRecent = releaseAge < 7 * 24 * 60 * 60 * 1000;
  const visibleHighlights = highlights.slice(0, 2);

  return (
    <div
      className={`release-notification-banner tw-mb-4 tw-transition-all tw-duration-300 ${
        isVisible ? 'tw-opacity-100 tw-translate-y-0' : 'tw-opacity-0 tw--translate-y-2'
      }`}
    >
      <div
        role="status"
        className="tw-relative tw-overflow-hidden tw-rounded-xl tw-border tw-border-indigo-200 tw-bg-white tw-shadow-sm"
      >
        <div className="tw-absolute tw-inset-y-0 tw-left-0 tw-w-1 tw-bg-indigo-600" aria-hidden="true" />

        <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4 tw-pl-5 sm:tw-flex-row sm:tw-items-center sm:tw-gap-4 sm:tw-px-5 sm:tw-py-3.5">
          <div className="tw-hidden tw-h-10 tw-w-10 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-indigo-50 tw-text-indigo-600 sm:tw-flex">
            <MdRocketLaunch size={20} aria-hidden="true" />
          </div>

          <div className="tw-min-w-0 tw-flex-1">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">
              <span className="tw-inline-flex tw-items-center tw-rounded-md tw-bg-indigo-50 tw-px-2 tw-py-1 tw-font-mono tw-text-[11px] tw-font-bold tw-text-indigo-700">
                v{version}
              </span>
              {isRecent && (
                <span className="tw-rounded-full tw-bg-emerald-50 tw-px-2 tw-py-1 tw-text-[11px] tw-font-semibold tw-text-emerald-700">
                  New release
                </span>
              )}
              <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-[13px] tw-font-semibold tw-text-slate-800">
                {title}
              </span>
            </div>

            {visibleHighlights.length > 0 && (
              <div className="tw-mt-2 tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-1">
                {visibleHighlights.map((highlight, index) => (
                  <span key={`${version}-${index}`} className="tw-inline-flex tw-min-w-0 tw-max-w-full tw-items-center tw-gap-1.5 tw-text-xs tw-text-slate-500">
                    <MdCheckCircle size={13} className="tw-flex-shrink-0 tw-text-emerald-500" aria-hidden="true" />
                    <span className="tw-truncate">{highlight}</span>
                  </span>
                ))}
                {highlights.length > visibleHighlights.length && (
                  <span className="tw-text-xs tw-text-slate-400">
                    +{highlights.length - visibleHighlights.length} more
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="tw-flex tw-items-center tw-gap-2 tw-self-end tw-flex-shrink-0 sm:tw-self-auto">
            <button
              type="button"
              onClick={handleViewChangelog}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-indigo-200 tw-bg-indigo-50 tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-indigo-700 tw-transition-colors hover:tw-border-indigo-300 hover:tw-bg-indigo-100 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-300 focus:tw-ring-offset-2"
            >
              View changelog <FiArrowRight size={13} aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="tw-rounded-lg tw-p-2 tw-text-slate-400 tw-transition-colors hover:tw-bg-slate-100 hover:tw-text-slate-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-300 focus:tw-ring-offset-2"
              aria-label="Dismiss release notification"
            >
              <FiX size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReleaseNotificationBanner;
